/** Group Billing / Approved Awaiting Billing helpers (shared client + server). */

import {
  buildCommercialInstalmentSchedule,
} from "./proposalCommercialPayable";
import {
  resolveApprovedProposalBillingCommercial,
  type ApprovedProposalBillingCommercialSource,
} from "./approvedProposalBillingCommercial";

export type BillingMode = "standard" | "group";

export type GroupBillingStatus =
  | "awaiting_approval"
  | "approved_awaiting_billing"
  | "partially_billed"
  | "fully_billed"
  | "collected";

export const GROUP_BILLING_STATUS_LABELS: Record<GroupBillingStatus, string> = {
  awaiting_approval: "Awaiting Approval",
  approved_awaiting_billing: "Approved Awaiting Billing",
  partially_billed: "Partially Billed",
  fully_billed: "Fully Billed",
  collected: "Collected",
};

export function isGroupBillingMode(mode: string | null | undefined): boolean {
  return String(mode ?? "").toLowerCase() === "group";
}

/** True when approval/WON must skip auto invoice create/send. */
export function shouldDeferInvoiceOnApproval(header: {
  billingMode?: string | null;
}): boolean {
  return isGroupBillingMode(header.billingMode);
}

export function isProposalApprovedForBilling(header: {
  status?: string | null;
  approvedAt?: Date | string | null;
  pipelineStage?: string | null;
}): boolean {
  if (header.approvedAt) return true;
  if (String(header.status ?? "").toLowerCase() === "approved") return true;
  const stage = String(header.pipelineStage ?? "").toLowerCase();
  return stage === "projected" || stage === "completed";
}

/**
 * Approved Awaiting Billing:
 * Group Billing + approved + no Approval invoice yet.
 */
export function isApprovedAwaitingBilling(params: {
  billingMode?: string | null;
  status?: string | null;
  approvedAt?: Date | string | null;
  pipelineStage?: string | null;
  hasApprovalInvoice: boolean;
  coveredByBillingEvent?: boolean;
}): boolean {
  if (!isGroupBillingMode(params.billingMode)) return false;
  if (!isProposalApprovedForBilling(params)) return false;
  if (params.hasApprovalInvoice) return false;
  return true;
}

export function deriveGroupBillingStatus(params: {
  billingMode?: string | null;
  status?: string | null;
  approvedAt?: Date | string | null;
  pipelineStage?: string | null;
  hasApprovalInvoice: boolean;
  invoicePaid: boolean;
  coveredByBillingEvent?: boolean;
}): GroupBillingStatus {
  if (!isProposalApprovedForBilling(params)) return "awaiting_approval";
  if (params.invoicePaid) return "collected";
  // Invoice row is the source of truth — never show billed from billing-event alone
  if (params.hasApprovalInvoice) {
    return "fully_billed";
  }
  return "approved_awaiting_billing";
}

export function formatApprovalDateLabel(approvedAt: Date | string | null | undefined): string | null {
  if (!approvedAt) return null;
  const d = approvedAt instanceof Date ? approvedAt : new Date(approvedAt);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString("en-SG", { day: "numeric", month: "short", year: "numeric" });
}

export function formatLinkedProposalDisplayLine(params: {
  proposalRef: string;
  approvedAt?: Date | string | null;
  awaitingLabel?: string;
}): { ref: string; statusLine: string } {
  const dateLabel = formatApprovalDateLabel(params.approvedAt);
  return {
    ref: params.proposalRef,
    statusLine: dateLabel ? `Approved ${dateLabel}` : (params.awaitingLabel ?? "Awaiting Approval"),
  };
}

/**
 * Currently collectible first-tranche / deposit amount (not full proposal value).
 *
 * P0 Billing Freeze: when an accepted snapshot (or original-contract hint) is present,
 * amounts are derived from the ORIGINAL approved proposal — never the post-VO living Project Value.
 * VO amounts must not be folded into this figure.
 */
export function computeCollectibleFirstTrancheCommercial(
  header: ApprovedProposalBillingCommercialSource,
): { exGstBase: number; gstAmount: number; payableWithGst: number } {
  const commercial = resolveApprovedProposalBillingCommercial(header);
  const raw = commercial.paymentTermsJson;
  const rows = Array.isArray(raw)
    ? raw.filter((r: any) => {
        const fixed = r.fixedAmount != null && Number(r.fixedAmount) > 0;
        const pct = r.percentage != null && Number(r.percentage) > 0;
        return fixed || pct;
      })
    : [];

  const firstRow = rows[0] as { fixedAmount?: number; percentage?: number; stage?: string } | undefined;
  if (firstRow?.fixedAmount != null && Number(firstRow.fixedAmount) > 0 && !(Number(firstRow.percentage) > 0)) {
    const fixed = Math.round(Number(firstRow.fixedAmount) * 100) / 100;
    return { exGstBase: fixed, gstAmount: 0, payableWithGst: fixed };
  }

  const percentage =
    firstRow && Number(firstRow.percentage) > 0 ? Number(firstRow.percentage) : 100;
  const remainder = Math.max(0, 100 - percentage);
  const schedule = buildCommercialInstalmentSchedule({
    totalAmount: commercial.totalAmount,
    retailPricingJson: (commercial.retailPricingJson as Record<string, unknown> | null) ?? null,
    invoiceGstEnabled: commercial.invoiceGstEnabled,
    paymentTerms:
      remainder > 0.004
        ? [
            { stage: String(firstRow?.stage ?? "Deposit"), percentage },
            { stage: "Remainder", percentage: remainder },
          ]
        : [{ stage: String(firstRow?.stage ?? "Deposit"), percentage }],
  });
  const first = schedule[0];
  return {
    exGstBase: first?.exGstBase ?? 0,
    gstAmount: first?.gstAmount ?? 0,
    payableWithGst: first?.payableWithGst ?? 0,
  };
}

export function computeCollectibleFirstTrancheAmount(
  header: ApprovedProposalBillingCommercialSource,
): number {
  return computeCollectibleFirstTrancheCommercial(header).exGstBase;
}

export function daysSinceApproval(approvedAt: Date | string, now: Date = new Date()): number {
  const d = approvedAt instanceof Date ? approvedAt : new Date(approvedAt);
  if (Number.isNaN(d.getTime())) return 0;
  const ms = now.getTime() - d.getTime();
  return Math.floor(ms / (24 * 60 * 60 * 1000));
}

/** First-tranche Approval invoice instalmentStage for a proposal row. */
export function approvalInstalmentStage(proposalId: number): string {
  return `Approval-${proposalId}`;
}

/**
 * Historical and current invoice stage keys for the first required payment.
 * Keep every deposit/payment gate on this authority so Approval-* and PPT-*-0
 * cannot drift between workflows.
 */
export function firstPaymentInstalmentStages(proposalId: number): [string, string] {
  return [approvalInstalmentStage(proposalId), `PPT-${proposalId}-0`];
}

/**
 * Deterministic obligation key for first-approval billing.
 * Unique while claimed; released (deleted) on void so recreate can reclaim.
 */
export function approvalBillingObligationKey(proposalId: number): string {
  return approvalInstalmentStage(proposalId);
}

/**
 * Tranche-specific billing obligation authority (Finance-selected Group Billing).
 * Independent of legacy Approval-{proposalId} deposit keys — do not rewrite historical keys.
 */
export function trancheBillingObligationKey(revenueTrancheId: number): string {
  return `GB-Tranche:${revenueTrancheId}`;
}

/** Allocation unique key for a Finance-selected tranche obligation. */
export function groupBillingTrancheAllocationObligationKey(revenueTrancheId: number): string {
  return `GB-Alloc:${trancheBillingObligationKey(revenueTrancheId)}`;
}

export function parseTrancheBillingObligationKey(
  key: string | null | undefined,
): number | null {
  const m = /^GB-Tranche:(\d+)$/.exec(String(key ?? "").trim());
  if (!m) return null;
  const id = Number(m[1]);
  return Number.isFinite(id) && id > 0 ? id : null;
}

export function parseGroupBillingTrancheAllocationObligationKey(
  key: string | null | undefined,
): number | null {
  const raw = String(key ?? "").trim();
  const m = /^GB-Alloc:GB-Tranche:(\d+)$/.exec(raw);
  if (!m) return null;
  const id = Number(m[1]);
  return Number.isFinite(id) && id > 0 ? id : null;
}

/**
 * Customer-facing / Xero / PDF line for a Group Billing allocation.
 * Prefer proposal title (or ref) + actual tranche stage — not hardcoded "Deposit for".
 */
export function formatGroupBillingAllocationLineDescription(params: {
  proposalTitle?: string | null;
  proposalRef?: string | null;
  stageLabel?: string | null;
  instalmentStage?: string | null;
}): string {
  const title = String(params.proposalTitle ?? "").trim();
  const ref = String(params.proposalRef ?? "").trim();
  const name = title || ref || "Proposal";
  let stage = String(params.stageLabel ?? "").trim();
  if (!stage) {
    const instalment = String(params.instalmentStage ?? "").trim();
    if (/^Approval-\d+$/i.test(instalment)) {
      stage = "Deposit";
    } else if (instalment && !/^GroupBilling-\d+$/i.test(instalment) && !/^GB-Tranche:\d+$/i.test(instalment)) {
      stage = instalment;
    } else {
      stage = "Payment";
    }
  }
  return `${name} — ${stage}`;
}

/**
 * Request-level idempotency key for a group billing generate call.
 * Prefer a client UUID for double-click/retry; fall back to obligation set fingerprint.
 */
export function buildGroupBillingRequestIdempotencyKey(params: {
  billingGroupId: number;
  mode: "full_group" | "approved_only" | "selected_tranches";
  proposalIds: number[];
  clientToken?: string | null;
}): string {
  const token = String(params.clientToken ?? "").trim();
  if (token) return `gb-req:${params.billingGroupId}:${params.mode}:${token}`;
  const ids = [...params.proposalIds].sort((a, b) => a - b).join(",");
  return `gb-set:${params.billingGroupId}:${params.mode}:${ids || "none"}`;
}

/** Idempotency for Finance-selected tranche Group Billing (customer-scoped). */
export function buildFinanceSelectedGroupBillingIdempotencyKey(params: {
  customerId: number;
  revenueTrancheIds: number[];
  clientToken?: string | null;
}): string {
  const token = String(params.clientToken ?? "").trim();
  if (token) return `gb-fin-req:${params.customerId}:${token}`;
  const ids = [...params.revenueTrancheIds].sort((a, b) => a - b).join(",");
  return `gb-fin-set:${params.customerId}:${ids || "none"}`;
}

/** Event total must equal sum of committed invoice allocations. */
export function sumCommittedAllocationAmounts(
  allocations: Array<{ amountSgd: number }>,
): number {
  const sum = allocations.reduce((acc, a) => acc + (Number(a.amountSgd) || 0), 0);
  return Math.round(sum * 100) / 100;
}
